import type { PendingSettlementLines, SettlementStatus, SettlementView } from '@cuito/types';
import { apiClient } from '../lib/apiClient';
import type { Paginated } from './catalog';

/**
 * 结算的五个调用（`04-api-contract.md` §15 端点表 1–5；UI 规范 554 行的 `/settlements`）。
 *
 * ## 三个必须写下来的口径
 *
 * 1. **`counterpartyTenantId` 是「对方」= 收款方 = 货主**（§15.0）：调用方恒为**付款方**（持货人）。
 *    前端把这两个角色搞反的后果不是报错，而是**结算单的收付方向反了**。
 * 2. **范围只有两种模式**（§15.0）：给 `eventId`，或给成对的 `periodStart`/`periodEnd`。
 *    两者都不给 ⇒ `422 SETTLEMENT_SCOPE_REQUIRED`；给一个不给另一个 ⇒ 同一码。
 *    本层**不预判**（不在前端拼「最近 30 天」这种缺省范围）：范围是财务口径，缺省必须由人给。
 * 3. **`pendingLines` 与 `generate` 共用同一个纯函数**（§15.3 的判据：同范围的两次调用四个金额逐个相等）
 *    ⇒ 前端的**预览**与**提交**必须发同样的参数形状，`GenerateSettlementDialog` 用同一个
 *    `settlementScopeOf()` 拼两次请求体。
 *
 * ## 金额一律字符串
 *
 * 四个金额（`totalSales` / `refundDeduction` / `commission` / `netSettlement`）**只显示、不计算**。
 * 前端做减法（甚至做 `Number()`）就是第二份口径：`netSettlement` 是服务端算好的派生量。
 *
 * ## `settle` 是**两参**，没有 `Idempotency-Key`（A-P7-46）
 *
 * 冻结文档 §五与契约 §15.4 都是 `settle(id, body)`：契约里**没有**这个请求头，
 * 标记已结算是**单向状态跃迁**（`PENDING → SETTLED`），重复调用由 `409 SETTLEMENT_ALREADY_SETTLED`
 * 表达，不靠幂等键重放。因此本层不收键、**不生成 uuid**，弹窗也不持键。
 */

export type {
  PendingSettlementLines,
  SettlementDirection,
  SettlementLineView,
  SettlementStatus,
  SettlementView,
} from '@cuito/types';

/** `POST /settlements/generate`（§15.1）；两种范围模式**至少给一种** */
export interface GenerateSettlementRequest {
  /** 对方（收款方 / 货主）社团 id */
  counterpartyTenantId: string;
  /** 自己的展会（`Event.id`）；与期间模式二选一 */
  eventId?: string;
  periodStart?: string;
  periodEnd?: string;
  note?: string | null;
}

/** `GET /settlements`（§15.2） */
export interface ListSettlementsQuery {
  eventId?: string;
  counterpartyTenantId?: string;
  status?: SettlementStatus;
  /** 不传 ⇒ 不过滤（正向与反向都要能被查到） */
  direction?: 'FORWARD' | 'REVERSE';
  page?: number;
  pageSize?: number;
}

/** `GET /settlements/pending-lines`（§15.3）：与 `generate` 同一套范围，只是只读 */
export interface PendingLinesQuery {
  counterpartyTenantId: string;
  eventId?: string;
  periodStart?: string;
  periodEnd?: string;
}

/** `POST /settlements/:id/settle`（§15.4）的请求体 */
export interface SettleSettlementRequest {
  settlementDate: string;
  voucherUrl?: string;
  note?: string;
}

/**
 * `POST /settlements/:id/settle` 的响应（§15.4 逐字：只有五个键）。
 *
 * ⚠️ 它不是 `SettlementView`（没有四个金额与 `lineCount`）—— 前端拿到它之后必须**重取详情或列表**
 * 才能刷新金额，不许把响应体当详情用（那会让刚结算的单据在页面上显示 `undefined` 金额）。
 * 该形状**没有**进 `packages/types`（冻结文档 §二 的清单里没有它）⇒ 本文件就地声明，
 * 并作为**待控制器裁决 D-15** 提交（是否要链 A 把它搬进 `@cuito/types` 的 `settlement.ts`）。
 */
export interface SettleSettlementResult {
  id: string;
  status: SettlementStatus;
  settlementDate: string | null;
  voucherUrl: string | null;
  note: string | null;
}

/** 一个弹窗里「范围」的可序列化形状（预览与提交**共用**，防止两处口径漂移） */
export interface SettlementScopeInput {
  eventId: string;
  periodStart: string;
  periodEnd: string;
}

/**
 * 范围表单 → 契约请求体片段。空串一律丢弃；
 * **期间必须成对**（`periodStart` 与 `periodEnd` 同时有值）—— 只给一个时**两个都不发**：
 * 让服务端给出 `422 SETTLEMENT_SCOPE_REQUIRED`，而不是前端自己拼一个半截范围
 * （前端猜出来的范围会在服务端被当成合法输入，进而生成一张覆盖错误期间的单据）。
 */
export function settlementScopeOf(scope: SettlementScopeInput): Pick<
  GenerateSettlementRequest,
  'eventId' | 'periodStart' | 'periodEnd'
> {
  const out: Pick<GenerateSettlementRequest, 'eventId' | 'periodStart' | 'periodEnd'> = {};
  if (scope.eventId !== '') out.eventId = scope.eventId;
  if (scope.periodStart !== '' && scope.periodEnd !== '') {
    out.periodStart = scope.periodStart;
    out.periodEnd = scope.periodEnd;
  }
  return out;
}

/** 结算单列表的默认分页（契约 §1.3） */
export const SETTLEMENT_PAGE_SIZE = 20;

/**
 * 五个调用（契约 §15 端点表 1–5）。
 */
export const settlementsApi = {
  /** 首次 201 / 同范围重放 200 + `replayed: true`（§15.1 由 `@Res()` 完全接管状态码） */
  generate: (body: GenerateSettlementRequest) => apiClient.post<SettlementView>('/settlements/generate', body),

  /** 分页包裹，`createdAt desc, id desc`（§15.2） */
  list: (params: ListSettlementsQuery = {}) =>
    apiClient.get<Paginated<SettlementView>>('/settlements', { params }),

  /** 预结算预览（§15.3；**必须声明在 `:id` 之前**，前端只管拼路径） */
  pendingLines: (params: PendingLinesQuery) =>
    apiClient.get<PendingSettlementLines>('/settlements/pending-lines', { params }),

  /** 详情（含 `lines`） */
  detail: (id: string) => apiClient.get<SettlementView>(`/settlements/${id}`),

  /**
   * 标记已结算（§15.4）。已 `SETTLED` 再调 ⇒ `409 SETTLEMENT_ALREADY_SETTLED`
   * （文案 `结算单已结算，不可重复操作`）。
   *
   * **两参，无 `Idempotency-Key`**（A-P7-46）：重复结算是**状态错误**而不是「重放」，
   * 由服务端的 `409` 表达；本层既不收键也不生成 uuid。
   */
  settle: (id: string, body: SettleSettlementRequest) =>
    apiClient.post<SettleSettlementResult>(`/settlements/${id}/settle`, body),
};
